<template>
  <div
    v-if="fieldIsVisible()"
    :class="step.useTooltipValidation ? 'gc-mb-3' : ''"
  >
    <div
      v-if="!hideLabel"
      class="gc-flex gc-items-center gc-mb-2 ${field.component && field.component.thumbLabel === 'always' ? 'gc-mb-10' : ''}`"
    >
      <label class="gc-block gc-font-bold gc-text-sm gc-relative">
        {{ $t(fieldLabelString) }}
        <span
          v-if="field.required && showRequiredAsterisk"
          class="gc-text-primary gc-text-xl gc-leading-3"
          >*</span
        >
      </label>
      <helper-text-button
        v-if="$slots.helperText"
        @click="helpVisible = !helpVisible"
      />
      <span
        class="gc-ml-2"
        v-if="$slots.helperDialog || field.showHelperDialog"
      >
        <helper-dialog
          :width="helperDialogWidth"
          :title="
            $te(`${step.id}.formFields.${fieldName}.helperDialog.title`)
              ? $t(`${step.id}.formFields.${fieldName}.helperDialog.title`)
              : ''
          "
          v-if="field.showHelperDialog"
        >
          <slot name="helperDialog">
            <div
              :class="[
                `gc-helper-dialog-${step.id.toLowerCase()}-${fieldName.toLowerCase()}`,
              ]"
            >
              <div
                v-if="$te(`${fieldHelperDialogString}.html`)"
                class="gc-helper-dialog-wrapper"
                v-html="$t(`${fieldHelperDialogString}.html`)"
              />
              <span class="gc-helper-dialog-text" v-else>
                {{ $t(`${fieldHelperDialogString}.text`) }}
              </span>
              <gc-image
                :src="`${baseUrl}${helperDialogImagePath}`"
                class="gc-helper-dialog-image mt-4"
                v-if="helperDialogImagePath"
              />
            </div>
          </slot>
        </helper-dialog>
        <helper-dialog v-else>
          <slot name="helperDialog" />
        </helper-dialog>
      </span>
    </div>

    <div v-if="field.showDescription" class="gc-text-sm gc-mb-2 gc--mt-1">
      <span
        v-if="$te(`${step.id}.formFields.${fieldName}.htmlDescription`)"
        v-html="$t(`${step.id}.formFields.${fieldName}.htmlDescription`)"
      ></span>
      <span v-else>
        {{ $t(`${step.id}.formFields.${fieldName}.description`) }}
      </span>
    </div>

    <gc-alert
      v-show="helpVisible"
      v-if="$slots.helperText"
      :icon="false"
      dense
      type="info"
      text
      class="helper-text gc-mb-3 gc-py-3"
    >
      <slot name="helperText"></slot>
    </gc-alert>

    <transition :name="`tailwind-tooltip-${tooltipPosition}`">
      <gc-tailwind-tooltip
        v-if="step.useTooltipValidation && tooltipText"
        :position="tooltipPosition"
      >
        {{ tooltipText }}
      </gc-tailwind-tooltip>
    </transition>

    <component
      :is="controlComponent"
      v-bind="field.component"
      v-if="controlComponent"
      v-model="formData[fieldName]"
      @next-step="emitNextStep"
      :items="controlComponentItems"
      :label="
        controlComponentLabelString ? $t(controlComponentLabelString) : ''
      "
      :assistantStep="step"
      :fieldName="fieldName"
      :name="fieldName"
      :rules="controlValidators"
      :assistantContext="assistantContext"
      @focus="emitOnComponentFocus"
      @blur="emitOnComponentBlur"
      @input="emitOnComponentInput"
      @change="emitOnComponentChange"
      :hide-details="step.useTooltipValidation === true"
      :disabled="fieldIsDisabled() || isDisabled"
      ref="componentRef"
    >
      <template
        v-for="(_, name) in $scopedSlots"
        :slot="name"
        slot-scope="slotData"
        ><slot :name="name" v-bind="slotData"
      /></template>
      <template v-for="(_, name) in $slots" :slot="name">
        <slot :name="name" />
      </template>
    </component>
    <div v-else>No component found for this form field.</div>
  </div>
</template>

<script lang="ts">
import { PropType, computed, defineComponent, ref } from '@vue/composition-api';
import { Component } from 'vue';
import {
  AutocompleteItem,
  FormCheckboxComponentConfig,
  FormSelectComponentConfig,
  FormSwitchComponentConfig,
  StepDefinition,
} from '@/interfaces/step-definition';
import { FormFieldType } from '@/consts/input-types';
import HelperTextButton from '@/components/HelperTextButton.vue';
import GcAutocomplete from '@/components/primitives/GcAutocomplete.vue';
import GcCheckbox from '@/components/primitives/GcCheckbox.vue';
import GcDatePicker from '@/components/primitives/GcDatePicker.vue';
import GcSelect from '@/components/primitives/GcSelect.vue';
import GcSlider from '@/components/primitives/GcSlider.vue';
import GcSwitch from '@/components/primitives/GcSwitch.vue';
import GcTextField from '@/components/primitives/GcTextField.vue';
import GcTextArea from '@/components/primitives/GcTextArea.vue';
import GcTimePicker from '@/components/primitives/GcTimePicker.vue';
import GcTimePickerFull from '@/components/primitives/GcTimePickerFull.vue';
import MultipleChoice from '@/components/MultipleChoice.vue';
import GcAlert from '@/components/primitives/GcAlert.vue';
import GcDropzone from '@/components/primitives/GcDropzone.vue';
import GcImage from '@/components/primitives/GcImage.vue';
import { getValidators } from '@/lib/forms/validators';
import { isResettable } from '@/interfaces/reset';
import { AssistantContext } from '~/steps/steps';
import HelperDialog from '@/components/HelperDialog.vue';
import {
  getComponentLabelString,
  getOptionLabelString,
} from '@/lib/forms/helper';
import GcTailwindTooltip from '@/components/primitives/GcTailwindTooltip.vue';
import { TooltipPositions } from '@/consts/tooltip-positions';
import i18n from '@/plugins/i18n';

export default defineComponent({
  components: {
    GcAlert,
    GcAutocomplete,
    GcCheckbox,
    GcDatePicker,
    GcDropzone,
    GcImage,
    GcSelect,
    GcSlider,
    GcSwitch,
    GcTextArea,
    GcTextField,
    GcTimePicker,
    GcTimePickerFull,
    HelperDialog,
    HelperTextButton,
    MultipleChoice,
    GcTailwindTooltip,
  },
  methods: {
    // reset will reset the componentRef if it supports the 'reset' method.
    reset: function () {
      const componentRef = this.componentRef;
      if (isResettable(componentRef)) {
        componentRef.reset();
      }
    },
  },
  props: {
    assistantContext: {
      default: () => ({}),
      type: Object as PropType<AssistantContext>,
    },
    fieldName: {
      required: true,
      type: String,
    },
    formData: {
      default: () => ({}),
      required: true,
      type: Object as PropType<{ [key: string]: string }>,
    },
    hideLabel: {
      default: false,
      type: Boolean,
    },
    step: {
      default: () => ({}),
      type: Object as PropType<StepDefinition>,
    },
    autocompleteItems: {
      type: Array as PropType<string[] | number[] | AutocompleteItem[]>,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  setup: (props, { emit }) => {
    const helpVisible = ref(false);

    const tooltipText = ref('');

    const tooltipPosition: string =
      props.step.tooltipPosition ?? TooltipPositions.BOTTOM;

    const field = computed(() =>
      props.step.fields?.find((f) => f.name === props.fieldName),
    );
    const fieldLabelString = computed(
      () => `${props.step.id}.formFields.${props.fieldName}.label`,
    );

    if (!field.value) {
      throw Error('Could not find form field ' + props.fieldName);
    }

    const controlComponentMap: Record<number, string> = {
      [FormFieldType.Checkbox]: 'gc-checkbox',
      [FormFieldType.DatePicker]: 'gc-date-picker',
      [FormFieldType.MultipleChoice]: 'multiple-choice',
      [FormFieldType.Select]: 'gc-select',
      [FormFieldType.Slider]: 'gc-slider',
      [FormFieldType.Switch]: 'gc-switch',
      [FormFieldType.TextArea]: 'gc-text-area',
      [FormFieldType.TextField]: 'gc-text-field',
      [FormFieldType.TimePicker]: 'gc-time-picker',
      [FormFieldType.TimePickerFull]: 'gc-time-picker-full',
      [FormFieldType.FileUpload]: 'gc-dropzone',
      [FormFieldType.Autocomplete]: 'gc-autocomplete',
    };
    const controlComponent = computed(
      () => field.value && controlComponentMap[field.value.type],
    );

    // This label is used to get translated labels if needed within component like VCheckbox
    const controlComponentLabelString = computed(() =>
      field.value?.component &&
      (
        field.value.component as
          | FormCheckboxComponentConfig
          | FormSwitchComponentConfig
      ).showLabel
        ? getComponentLabelString(props.step.id, props.fieldName)
        : '',
    );

    // This label is used to get translated items if needed within component like VSelect or VAutocomplete
    const controlComponentItems = computed(() => {
      if (field.value?.component) {
        if ((field.value.component as FormSelectComponentConfig).items) {
          return (field.value.component as FormSelectComponentConfig).items
            .map((item) =>
              getOptionLabelString(props.step.id, props.fieldName, item),
            )
            .map((item) => i18n.t(item));
        }
      }
      if (props.autocompleteItems) {
        return props.autocompleteItems;
      }
      return undefined;
    });

    const fieldIsVisible = (): boolean => {
      if (!field.value?.isVisible) {
        return true;
      }

      return field.value?.isVisible(props.assistantContext);
    };

    const fieldIsDisabled = (): boolean => {
      if (!field.value?.isDisabled) {
        return false;
      }

      return field.value?.isDisabled(props.assistantContext);
    };

    const helperDialogWidth = computed(
      () => field.value?.helperDialog?.width ?? 500,
    );

    const helperDialogImagePath = computed(
      () => field.value?.helperDialog?.imagePath,
    );

    const showRequiredAsterisk =
      process.env.VUE_APP_SHOW_REQUIRED_ASTERISK === 'true';

    const getTooltipText = () => {
      const validators = (field.value && getValidators(field.value)) ?? [];
      for (const validator of validators) {
        const checkValueFromValidator: boolean | string =
          typeof validator === 'function' &&
          validator(props.formData[props.fieldName]);

        if (typeof checkValueFromValidator === 'string') {
          return checkValueFromValidator;
        } else if (checkValueFromValidator === false) {
          return 'No Error Message defined';
        }
      }
      return '';
    };

    const setTooltipText = () => {
      tooltipText.value = getTooltipText();
    };
    const unsetTooltipText = () => {
      tooltipText.value = '';
    };

    const controlValidators = computed(
      () => field.value && getValidators(field.value),
    );

    const emitNextStep = () => {
      emit('next-step');
    };

    const emitOnComponentChange = (event: Event) => {
      emit('component:change', event);
    };

    const emitOnComponentInput = (event: Event) => {
      emit('component:input', event);
    };

    const emitOnComponentBlur = (event: Event) => {
      setTooltipText();
      emit('component:blur', event);
    };

    const emitOnComponentFocus = (event: Event) => {
      unsetTooltipText();
      emit('component:focus', event);
    };

    const baseUrl = process.env.VUE_APP_BASE_URL;

    const fieldHelperDialogString = `${props.step.id}.formFields.${props.fieldName}.helperDialog`;

    const componentRef = ref<Component | null>(null);

    return {
      baseUrl,
      FormFieldType,
      controlComponent,
      componentRef,
      controlComponentItems,
      controlComponentLabelString,
      controlValidators,
      emitNextStep,
      emitOnComponentBlur,
      emitOnComponentFocus,
      emitOnComponentChange,
      emitOnComponentInput,
      field,
      fieldIsDisabled,
      fieldIsVisible,
      fieldLabelString,
      fieldHelperDialogString,
      helpVisible,
      helperDialogWidth,
      helperDialogImagePath,
      showRequiredAsterisk,
      setTooltipText,
      unsetTooltipText,
      tooltipText,
      tooltipPosition,
    };
  },
});
</script>

<style lang="scss">
.helper-text {
  font-size: 95%;

  p:last-child {
    margin-bottom: 0 !important;
  }

  img {
    max-width: 100%;
    height: auto;
    vertical-align: middle;
    border-radius: 4px;
  }
}
</style>
